import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Column } from "primereact/column";
import { InputSwitch } from "primereact/inputswitch";

import type { LeaveTypeUiT } from "../ui/models/leaveType.model";
import type {
  CreateLeaveTypeFormValuesT,
  UpdateLeaveTypeTypeFormValuesT,
} from "../domain/types/leaveType.types";
import type { LeaveTypeEntityT } from "../domain/entities/leaveType.entity";

import { bodyClassName, headerClassName } from "assets/customStyles";

import { GeneralNames, TableNames } from "namesConstants/names";

import { LEAVE_TYPES_COLUMNS, TABLE_COLUMN_KEYS } from "../constants/columns";
import { LEAVE_TYPES_STRINGS } from "../constants/strings";

import { FullLoading } from "components/FullLoading";
import { Toast } from "components/toast";

import { ColaboraTable, TableActions } from "components/v2";

import { useExportToExcel } from "../hooks/useExportToExcel";
import { useLeaveTypes } from "../hooks";

import { useCurrentCompany } from "hooks/useCurrentCompany";
import { useFilteredTableData } from "hooks/useFilteredTableData";
import { useQueryError } from "hooks/useQueryError";
import { useTableBehavior } from "hooks/useTableBehavior";
import useReload from "hooks/useReload";

import { ConfirmPopup } from "primereact/confirmpopup";

import LeaveTypeDialog from "./LeaveTypeDialog";
import TableForm from "./TableForm";

const LeaveTypesTable = () => {
  const queryClient = useQueryClient();

  const companyInfo = useCurrentCompany();

  const [showDialog, setShowDialog] = useState(false);
  const [activeType, setActiveType] = useState<LeaveTypeEntityT | null>(null);

  const {
    data: leaveTypes = [],
    isLoading,
    refetch,
    isError,
    error,
    createLeaveType: { isPending: isCreating, mutateAsync: createLeaveType },
    updateLeaveType: { isPending: isUpdating, mutateAsync: updateLeaveType },
    deleteLeaveType: { isPending: isDeleting, mutateAsync: deleteLeaveType },
    toggleLeaveTypeActiveStatus: { mutateAsync: toggleLeaveTypeActiveStatus },
  } = useLeaveTypes({
    colaboraInstanceId: companyInfo?.instanceId,
  });

  useReload([refetch]);
  useQueryError({
    isError: isError,
    error: error,
    message: LEAVE_TYPES_STRINGS.TYPES_ERROR_MESSAGE,
  });

  const { exportToExcel, isExporting } = useExportToExcel();

  const {
    searchTextFilter,
    sort,
    tableFilters,
    resetSearchTextFilter,
    setSearchTextFilter,
    setSort,
  } = useTableBehavior({
    initialSort: [{ field: TABLE_COLUMN_KEYS.CODE, order: 1 }],
  });

  const getSearchableFields = useCallback(
    (item: LeaveTypeUiT) => [item.code, item.name],
    [],
  );

  const { fullyFilteredData: fullyFilteredLeaveTypes } = useFilteredTableData({
    data: leaveTypes,
    tableFilters,
    searchTextFilter,
    getSearchableFields,
  });

  const handleOnOpenDialog = () => setShowDialog(true);

  const handleOnHideDialog = () => {
    setShowDialog(false);

    if (activeType !== null) {
      setActiveType(null);
    }
  };

  const handleOnSubmit = async (formValues: CreateLeaveTypeFormValuesT) => {
    try {
      if (activeType === null) {
        const { succeeded, failed } = await createLeaveType({
          data: formValues,
        });

        if (succeeded.length > 0) {
          Toast("success", LEAVE_TYPES_STRINGS.CREATE_SUCCESS);
          handleOnHideDialog();
        }

        if (failed.length > 0) {
          Toast(
            "warning",
            LEAVE_TYPES_STRINGS.CREATE_PARTIAL_FAILURE,
            failed[0].reason,
          );
        }
      } else {
        const type: UpdateLeaveTypeTypeFormValuesT = {
          id: activeType.id,
          code: formValues.code,
          name: formValues.name ?? "",
          description: activeType.code,
          icon: formValues.icon,
          isJustified: formValues.isJustified,
          incidenceTypeId: formValues.incidenceTypeId,
        };

        await updateLeaveType({
          type,
          policy: formValues,
          policyId: activeType?.policy?.id ?? null,
        });

        Toast("success", LEAVE_TYPES_STRINGS.UPDATE_SUCCESS);

        handleOnHideDialog();
      }
    } catch (error) {
      const errorKey = activeType === null ? "CREATE_ERROR" : "UPDATE_ERROR";
      Toast("warning", LEAVE_TYPES_STRINGS[errorKey], (error as Error).message);
    }
  };

  const handleOnUpdateType = (typeId: string) => {
    const data = queryClient.getQueryData<LeaveTypeEntityT[]>([
      "vacations/leave-types",
      { colaboraInstanceId: companyInfo?.instanceId },
    ]);

    if (data && data.length > 0) {
      const type = data.find((type) => type.id === typeId);

      if (type) {
        setActiveType(type);
        setShowDialog(true);
      }
    }
  };

  const onDeleteType = async (typeId: string) => {
    try {
      const { succeeded, failed } = await deleteLeaveType({
        typeIds: [typeId],
      });

      if (succeeded.length > 0) {
        Toast("success", LEAVE_TYPES_STRINGS.DELETE_SUCCESS);
      }

      if (failed.length > 0) {
        // When bulk deleting, it's necessary to decide whether to show a partial failure message or a complete failure message.
        Toast(
          "warning",
          LEAVE_TYPES_STRINGS.DELETE_PARTIAL_FAILURE,
          failed[0].reason,
        );
      }
    } catch (error) {
      Toast(
        "error",
        LEAVE_TYPES_STRINGS.DELETE_ERROR,
        (error as Error).message,
      );
    }
  };

  const onToggleActiveStatus = async (typeId: string, isActive: boolean) => {
    try {
      await toggleLeaveTypeActiveStatus({ typeId, isActive });
    } catch (error) {
      Toast(
        "error",
        LEAVE_TYPES_STRINGS.TOGGLE_ACTIVE_STATUS_ERROR,
        (error as Error).message,
      );
    }
  };

  const switchBodyTemplate = (rowData: LeaveTypeUiT) => (
    <InputSwitch
      checked={rowData.active}
      onChange={() => onToggleActiveStatus(rowData.id, !rowData.active)}
    />
  );

  const actionsBodyTemplate = (rowData: LeaveTypeUiT) => (
    <TableActions
      rowData={rowData}
      onEditClicked={handleOnUpdateType}
      onDeleteConfirm={onDeleteType}
      editDisabled={isDeleting}
      deleteDisabled={!rowData.isCustom || isDeleting}
      editTooltip={LEAVE_TYPES_STRINGS.TEXT_TOOLTIP_EDIT}
      deleteTooltip={LEAVE_TYPES_STRINGS.TEXT_TOOLTIP_DELETE}
      deleteConfirmMessage={LEAVE_TYPES_STRINGS.DELETE_CONFIRM_MESSAGE}
    />
  );

  const handleExportToExcel = async () => {
    if (isExporting) return;

    if (fullyFilteredLeaveTypes.length === 0) {
      Toast("info", LEAVE_TYPES_STRINGS.EXPORT_NO_DATA);
      return;
    }

    try {
      await exportToExcel({
        data: fullyFilteredLeaveTypes,
        sortConfig: sort,
      });

      Toast("success", LEAVE_TYPES_STRINGS.EXPORT_SUCCESS);
    } catch (error) {
      Toast(
        "error",
        LEAVE_TYPES_STRINGS.EXPORT_ERROR,
        (error as Error).message || "No se pudo generar el archivo",
      );
    }
  };

  return (
    <section
      className="h-full w-full border rounded-md shadow-2xl p-2"
      aria-label="Listado de tipos de permisos"
      aria-busy={isLoading || isExporting || isDeleting}
      data-testid="leave-types-table-section"
    >
      <ConfirmPopup />
      <div aria-live="assertive" aria-atomic="true">
        {isExporting && <FullLoading loadingState />}
      </div>
      <div className="w-full h-full flex flex-col gap-2">
        <TableForm
          setSearchTextFilter={setSearchTextFilter}
          resetSearchTextFilter={resetSearchTextFilter}
          onExportToExcel={handleExportToExcel}
          onOpenTypeDialog={handleOnOpenDialog}
        />
        <ColaboraTable
          data={fullyFilteredLeaveTypes}
          isLoadingData={isLoading}
          sort={sort}
          onChangeSort={setSort}
          filters={searchTextFilter}
          emptyMessage={GeneralNames.GeneralEmptyMessage}
          currentPageReportTemplate={LEAVE_TYPES_STRINGS.TEXT_TABLE_RESULTS}
          globalFilterFields={[TABLE_COLUMN_KEYS.CODE, TABLE_COLUMN_KEYS.NAME]}
        >
          <Column
            headerClassName={headerClassName}
            bodyClassName="!text-center !text-secondary-PCS004 !border-r-2 !border-b-0 !border-secondary-PCS004"
            style={{ width: "4rem", minWidth: "4rem" }}
            header="Activo"
            body={switchBodyTemplate}
            alignFrozen="left"
            frozen
          />
          {LEAVE_TYPES_COLUMNS.map((col) => {
            let body = col.body;

            if (col.field === TABLE_COLUMN_KEYS.ICON) {
              body = (rowData: LeaveTypeUiT) => {
                if (!rowData.icon) return <span>-</span>;

                const Icon = col.body(rowData);

                if (!Icon) return <span>-</span>;

                return <Icon size={30} />;
              };
            }
            return (
              <Column
                key={`${col.field}_leave_types`}
                headerClassName={headerClassName}
                style={col.style}
                field={col.field}
                header={col.header}
                body={body}
                bodyClassName={bodyClassName}
                align={col.align}
                sortable={col.sortable}
                alignFrozen="left"
                frozen={col.frozen}
              />
            );
          })}
          <Column
            headerClassName={headerClassName}
            style={{ minWidth: "8rem", width: "8rem" }}
            header={TableNames.TableActions}
            body={actionsBodyTemplate}
            bodyClassName={bodyClassName}
            align="center"
            alignFrozen="right"
            frozen
          />
        </ColaboraTable>
      </div>
      {showDialog && (
        <LeaveTypeDialog
          activeType={activeType}
          onHide={handleOnHideDialog}
          onSubmit={handleOnSubmit}
          isSaving={isCreating || isUpdating}
          visible
        />
      )}
    </section>
  );
};

export default LeaveTypesTable;
